/**
 * First-time reviewer tour (C11): three coach marks, no library.
 *
 *   1. Release Board: the "In review" column (`data-tour="release-board"`)
 *   2. Release drawer: the stamp button = Release (`data-tour="release-stamp"`)
 *   3. Release drawer: Request changes beside the chat composer (`data-tour="release-changes"`)
 *
 * Shown once per person (any brand): only to a server-confirmed reviewer of this brand while the
 * badges say `tourSeen === false`. Done or Skip stamps the `release_tour` surface and patches the
 * cached badges (no invalidation). Steps 2 and 3 appear the first time the drawer opens; opening
 * the drawer before finishing step 1 continues there.
 *
 * The drawer is a modal Radix dialog that traps focus, so its marks are portalled INTO the dialog
 * element; the board mark goes to the body.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useReleaseBadges, useReleaseBrandGate } from '@/hooks/useReleaseSections';
import { releaseSectionsService } from '@/services/releaseSections.service';
import type { ReleaseBadges } from '@/types/releaseSections';
import { cn } from '@/utils/cn';

type TourStep = 1 | 2 | 3;

interface StepConfig {
  anchor: string;
  titleKey: string;
  bodyKey: string;
}

const STEPS: Record<TourStep, StepConfig> = {
  1: { anchor: 'release-board', titleKey: 'release_tour_step1_title', bodyKey: 'release_tour_step1_body' },
  2: { anchor: 'release-stamp', titleKey: 'release_tour_step2_title', bodyKey: 'release_tour_step2_body' },
  3: { anchor: 'release-changes', titleKey: 'release_tour_step3_title', bodyKey: 'release_tour_step3_body' },
};

const MARK_WIDTH = 300;
const EDGE = 8;
const GAP = 12;
const MOBILE_MAX_PX = 639;
/** Tablet band (sm..lg): the board columns are full width, so step 1 sits above / below the column. */
const DESKTOP_MIN_PX = 1024;
/** Estimated mark height until the real one is measured */
const MARK_HEIGHT_FALLBACK = 150;
/** Keep marks clear of the app navbar when placed above an anchor */
const NAVBAR_CLEARANCE = 64;
const POLL_MS = 200;

interface Placement {
  /** Mobile bottom sheet */
  sheet: boolean;
  top: number;
  left: number;
  /** Arrow x inside the mark (px), null when there is no arrow */
  arrowLeft: number | null;
  /** The mark sits above its anchor, so its arrow points down. */
  arrowBelow?: boolean;
  /** Anchor box relative to the portal container */
  ring: { top: number; left: number; width: number; height: number };
}

const samePlacement = (a: Placement | null, b: Placement | null): boolean =>
  a === b ||
  (!!a &&
    !!b &&
    a.sheet === b.sheet &&
    a.top === b.top &&
    a.left === b.left &&
    a.arrowLeft === b.arrowLeft &&
    a.arrowBelow === b.arrowBelow &&
    a.ring.top === b.ring.top &&
    a.ring.left === b.ring.left &&
    a.ring.width === b.ring.width &&
    a.ring.height === b.ring.height);

const findAnchor = (name: string): HTMLElement | null =>
  document.querySelector<HTMLElement>(`[data-tour="${name}"]`);

/** Where the mark lives: the drawer dialog for steps 2/3 (focus trap), else the body. */
const findContainer = (anchor: HTMLElement, step: TourStep): HTMLElement =>
  step === 1 ? document.body : (anchor.closest<HTMLElement>('[role="dialog"]') ?? document.body);

function computePlacement(
  anchor: HTMLElement,
  container: HTMLElement,
  step: TourStep,
  markHeight: number
): Placement | null {
  const a = anchor.getBoundingClientRect();
  if (a.width === 0 && a.height === 0) return null;
  const inBody = container === document.body;
  const c = inBody ? { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight } : container.getBoundingClientRect();
  const phone = window.innerWidth <= MOBILE_MAX_PX;
  const tablet = !phone && window.innerWidth < DESKTOP_MIN_PX;
  const ring = { top: a.top - c.top, left: a.left - c.left, width: a.width, height: a.height };
  // Board step on a phone: docked above the bottom nav (the column fills the screen).
  if (phone && step === 1) return { sheet: true, top: 0, left: 0, arrowLeft: null, ring };

  const width = phone ? c.width - EDGE * 2 : Math.min(MARK_WIDTH, c.width - EDGE * 2);
  const anchorCenter = ring.left + a.width / 2;
  const clampLeft = (value: number): number => Math.max(EDGE, Math.min(value, c.width - width - EDGE));

  if (step === 1) {
    if (tablet) {
      // Never over the In review cards: just below the column, else just above it.
      const left = clampLeft(anchorCenter - width / 2);
      const room = window.innerHeight - a.bottom - GAP - EDGE;
      if (room >= markHeight) {
        return {
          sheet: false,
          top: Math.round(a.bottom + GAP),
          left: Math.round(left),
          arrowLeft: Math.round(Math.max(16, Math.min(anchorCenter - left, width - 16))),
          ring,
        };
      }
      if (a.top - markHeight - GAP >= NAVBAR_CLEARANCE) {
        return { sheet: false, top: Math.round(a.top - markHeight - GAP), left: Math.round(left), arrowLeft: null, ring };
      }
    }
    // Desktop: inside the column, just under its header, centred.
    const left = clampLeft(ring.left + (a.width - width) / 2);
    const viewportTop = Math.max(a.top + 64, EDGE);
    return { sheet: false, top: Math.round(viewportTop - c.top), left: Math.round(left), arrowLeft: null, ring };
  }

  // Drawer steps: the composer action sits at the bottom, so its mark opens above.
  // Tablet: right-aligned so the Preview tabs (left) stay visible.
  const left = tablet ? Math.max(EDGE, c.width - width - EDGE) : clampLeft(anchorCenter - width / 2);
  const arrow = anchorCenter - left;
  const belowTop = ring.top + a.height + GAP;
  const roomBelow = c.height - belowTop - EDGE;
  const roomAbove = ring.top - GAP - EDGE;
  const above = step === 3 || (roomBelow < markHeight && roomAbove >= markHeight);
  const desiredTop = above ? ring.top - markHeight - GAP : belowTop;
  const top = Math.max(EDGE, Math.min(desiredTop, c.height - markHeight - EDGE));
  return {
    sheet: false,
    top: Math.round(top),
    left: Math.round(left),
    arrowLeft:
      arrow >= 16 && arrow <= width - 16 &&
      (above ? top + markHeight <= ring.top : top >= ring.top + a.height)
        ? Math.round(arrow)
        : null,
    arrowBelow: above,
    ring,
  };
}

/** True when a dialog / sheet other than `own` is open and visible (Esc then belongs to it, not the tour). */
const otherDialogOpen = (own: HTMLElement | null): boolean =>
  Array.from(
    document.querySelectorAll<HTMLElement>(
      '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [aria-modal="true"]'
    )
  ).some((el) => {
    if (el === own || !el.isConnected || el.getAttribute('data-state') === 'closed') return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && getComputedStyle(el).visibility !== 'hidden';
  });

interface ReleaseTourProps {
  /** The release drawer is open */
  drawerOpen: boolean;
}

export const ReleaseTour = ({ drawerOpen }: ReleaseTourProps) => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { teamId } = useReleaseBrandGate();
  const { data: badges } = useReleaseBadges();

  const [step, setStep] = useState<TourStep>(1);
  /** Step 1 acknowledged with "Next": wait for the drawer */
  const [boardDone, setBoardDone] = useState(false);
  const [finished, setFinished] = useState(false);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const markRef = useRef<HTMLDivElement>(null);

  const eligible =
    !finished &&
    teamId !== null &&
    badges?.workflowActive === true &&
    badges.viewer?.isReviewer === true &&
    badges.tourSeen === false;

  // Opening the drawer during step 1 continues at step 2.
  const effectiveStep: TourStep = drawerOpen && step === 1 ? 2 : step;
  const visibleStep: TourStep | null = !eligible
    ? null
    : effectiveStep === 1
      ? drawerOpen || boardDone
        ? null
        : 1
      : drawerOpen
        ? effectiveStep
        : null;

  const seenMutation = useMutation({
    mutationFn: (id: number) => releaseSectionsService.markSeen(id, 'release_tour'),
    onSuccess: () => {
      // tourSeen is per person (any brand): patch every cached badges query, whatever the team.
      queryClient.setQueriesData<ReleaseBadges>(
        {
          predicate: (query) => {
            const key = query.queryKey;
            return (
              key.includes('approval') && key.includes('board') && key[key.length - 1] === 'badges'
            );
          },
        },
        (current) => (current ? { ...current, tourSeen: true } : current)
      );
    },
  });
  const { mutate: markSeen } = seenMutation;

  const finish = useCallback(() => {
    setFinished(true);
    if (teamId !== null) markSeen(teamId);
  }, [teamId, markSeen]);

  const next = useCallback(() => {
    if (visibleStep === 1) {
      setBoardDone(true);
      setStep(2);
    } else if (visibleStep === 2) {
      setStep(3);
    } else {
      finish();
    }
  }, [visibleStep, finish]);

  // Keep the mark glued to its anchor (drawer slides in, resizes, scrolls).
  useLayoutEffect(() => {
    if (visibleStep === null) {
      setPlacement(null);
      setContainer(null);
      return;
    }
    const update = () => {
      const anchor = findAnchor(STEPS[visibleStep].anchor);
      if (!anchor) {
        setPlacement(null);
        setContainer(null);
        return;
      }
      const target = findContainer(anchor, visibleStep);
      setContainer((prev) => (prev === target ? prev : target));
      const next = computePlacement(anchor, target, visibleStep, markRef.current?.offsetHeight || MARK_HEIGHT_FALLBACK);
      setPlacement((prev) => (samePlacement(prev, next) ? prev : next));
    };
    update();
    const id = window.setInterval(update, POLL_MS);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [visibleStep]);

  const shown = visibleStep !== null && placement !== null && container !== null;

  // Esc = skip (capture on window so the drawer / dialog does not also react), unless another
  // dialog / sheet is open: then Esc belongs to it and the tour stays (and is not marked seen).
  useEffect(() => {
    if (!shown) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const own = container !== document.body ? container : null;
      if (otherDialogOpen(own)) return;
      event.preventDefault();
      event.stopPropagation();
      finish();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [shown, finish, container]);

  // Focus moves to the mark when a step appears; back to where it was when the tour ends.
  const nextRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!shown) return;
    if (!returnFocusRef.current && document.activeElement instanceof HTMLElement) {
      returnFocusRef.current = document.activeElement;
    }
    nextRef.current?.focus({ preventScroll: true });
  }, [shown, visibleStep]);
  useEffect(() => {
    if (!finished) return;
    const el = returnFocusRef.current;
    returnFocusRef.current = null;
    if (el && document.contains(el)) el.focus({ preventScroll: true });
  }, [finished]);

  if (!shown || visibleStep === null) return null;
  const config = STEPS[visibleStep];
  const inBody = container === document.body;
  const positioning = inBody ? 'fixed' : 'absolute';
  const isLast = visibleStep === 3;

  return createPortal(
    <>
      {!placement.sheet || visibleStep !== 1 ? (
        <div
          aria-hidden="true"
          className={cn(
            positioning,
            'pointer-events-none z-toast rounded-xl ring-2 ring-primary-500 ring-offset-2',
            'animate-pulse motion-reduce:animate-none',
            visibleStep !== 1 && 'rounded-full'
          )}
          style={{
            top: placement.ring.top,
            left: placement.ring.left,
            width: placement.ring.width,
            height: placement.ring.height,
          }}
        />
      ) : null}
      <div
        ref={markRef}
        role="group"
        aria-labelledby="release-tour-title"
        aria-describedby="release-tour-body"
        className={cn(
          positioning,
          'z-toast bg-white text-gray-900 shadow-xl border border-primary-200',
          'animate-in fade-in-0 motion-reduce:animate-none',
          placement.sheet
            ? 'inset-x-2 bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom)+8px+var(--floating-stack-offset))] rounded-xl animate-in slide-in-from-bottom motion-reduce:animate-none'
            : 'rounded-xl'
        )}
        style={
          placement.sheet
            ? undefined
            : {
                top: placement.top,
                left: placement.left,
                width:
                  window.innerWidth <= MOBILE_MAX_PX
                    ? window.innerWidth - EDGE * 2
                    : Math.min(MARK_WIDTH, window.innerWidth - EDGE * 2),
              }
        }
      >
        {placement.arrowLeft !== null && (
          <span
            aria-hidden="true"
            className={cn(
              'absolute w-3 h-3 rotate-45 bg-white border-primary-200',
              placement.arrowBelow
                ? '-bottom-1.5 border-r border-b'
                : '-top-1.5 border-l border-t'
            )}
            style={{ left: placement.arrowLeft - 6 }}
          />
        )}
        <div className="p-4">
          <div aria-live="polite" aria-atomic="true">
            <p id="release-tour-title" className="text-sm font-semibold text-gray-900">
              {t(config.titleKey)}
            </p>
            <p id="release-tour-body" className="mt-1 text-sm text-gray-600">
              {t(config.bodyKey)}
            </p>
          </div>
          <div className="mt-3 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={finish}
              className="text-xs font-medium text-gray-600 hover:text-gray-900 rounded px-3 -ml-3 py-1.5 max-sm:min-h-[44px] border border-transparent focus:outline-none focus-visible:border-primary-600"
            >
              {t('release_tour_skip')}
            </button>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500" aria-hidden="true">
                {visibleStep}/3
              </span>
              <button
                ref={nextRef}
                type="button"
                onClick={next}
                className="h-[44px] sm:h-9 px-4 rounded-full bg-primary-600 text-primary-600-contrast text-sm font-semibold hover:bg-primary-700 hover:text-primary-700-contrast border border-transparent focus:outline-none focus-visible:border-primary-900"
              >
                {isLast ? t('release_tour_done') : t('release_tour_next')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    container
  );
};
